import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { OrganizationSwitcherFacade } from '@sona/angular-organization-domain';
import { SonaCurrentPlanBadgeComponent } from '@sona/angular-payments/components';
import { SonaIconComponent, SonaSpinnerComponent } from '@sona/angular-ui-components';
import { BrnSelectComponent, BrnSelectContentComponent, BrnSelectValueComponent } from '@spartan-ng/ui-select-brain';
import { HlmSelectContentDirective, HlmSelectOptionComponent, HlmSelectTriggerComponent, HlmSelectValueDirective } from '@spartan-ng/ui-select-helm';

@Component({
    selector: 'sona-organization-switcher',
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        SonaIconComponent,
        SonaCurrentPlanBadgeComponent,
        BrnSelectComponent,
        BrnSelectContentComponent,
        BrnSelectValueComponent,
        HlmSelectContentDirective,
        HlmSelectOptionComponent,
        HlmSelectTriggerComponent,
        HlmSelectValueDirective,
        FormsModule,
        SonaSpinnerComponent
    ],
    template: `
        <brn-select
            id="organization-switcher"
            class="tw-w-full"
            [ngModel]="_facade.currentOrganizationId()"
            (ngModelChange)="$event && _facade.switchOrganization($event)"
            [disabled]="_facade.isSwitchingOrganization()"
            [attr.aria-label]="'ORGANIZATION.SWITCHER.ACTION.SWITCH_ORGANIZATION' | translate"
        >
            <hlm-select-trigger class="tw-h-10 tw-min-w-40 hover:tw-bg-muted" size="sm">
                <div class="tw-flex tw-items-center tw-gap-2">
                    <span class="tw-contents tw-truncate tw-text-left tw-text-sm tw-font-medium tw-font-[Inter]">
                        <hlm-select-value />
                    </span>
                    <sona-current-plan-badge class="tw-mr-auto" />
                </div>

                <sona-icon
                    class="tw-ml-2"
                    name="caret-up-down"
                    size="small">
                </sona-icon>
            </hlm-select-trigger>

            <hlm-select-content class="tw-min-w-40">
                @if (organizationsResource.isLoading()) {
                    <sona-spinner class="tw-mx-auto" />
                } @else if (organizationsResource.error()) {
                    <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-p-2">
                        <sona-icon class="tw-text-destructive" name="alert-circle" size="small" />
                        <span class="tw-text-sm">
                            Ein Fehler ist aufgetreten.
                        </span>
                    </div>
                } @else if (organizationsResource.hasValue()) {
                    @for (org of organizationsResource.value(); track org.id) {
                        <hlm-option [value]="org.id" class="tw-whitespace-nowrap">
                            {{ org.name }}
                        </hlm-option>
                    }
                }
            </hlm-select-content>
        </brn-select>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SonaOrganizationSwitcherComponent {
    protected readonly _facade = inject(OrganizationSwitcherFacade);

    protected readonly organizationsResource = this._facade.getOrganizations();

    readonly canSwitchOrganizations = computed(() => {
        if (!this.organizationsResource.hasValue()) {
            return false;
        }

        const organizations = this.organizationsResource.value();

        return organizations != null && organizations.length > 1;
    });
}